import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Calculator, ArrowRight, PencilSimple } from "@phosphor-icons/react";

/**
 * Position sizer — computes qty and money to deploy based on user's
 * capital + risk-per-trade preference:
 *
 *   risk_amount  = capital * risk_per_trade_pct / 100
 *   per_share_risk = entry - stop_loss
 *   qty = floor(risk_amount / per_share_risk)
 *   investment = qty * entry
 *
 * Props: signal { entry, stop_loss, target, symbol }
 */
const fmt = (n) =>
  typeof n === "number"
    ? n.toLocaleString("en-IN", { maximumFractionDigits: 0 })
    : "—";

export default function PositionSizer({ signal, compact = false }) {
  const [prefs, setPrefs] = useState(null);
  const [editing, setEditing] = useState(false);
  const [capital, setCapital] = useState("");
  const [riskPct, setRiskPct] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/user/trade-prefs");
      setPrefs(data);
      setCapital(String(data.capital));
      setRiskPct(String(data.risk_per_trade_pct));
    } catch (e) {
      // Trade prefs are optional — if fetch fails we fall back to defaults
      // server-side. Logging helps spot infra issues during development.
      console.error("trade-prefs load", e);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const save = async (e) => {
    e?.preventDefault?.();
    setErr("");
    const c = Number(capital);
    const r = Number(riskPct);
    if (!(c > 0) || !(r > 0) || r > 10) {
      setErr("Capital > 0 and risk % 0.1 – 10");
      return;
    }
    setSaving(true);
    try {
      await api.put("/user/trade-prefs", { capital: c, risk_per_trade_pct: r });
      await load();
      setEditing(false);
    } catch (ex) {
      setErr(ex.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (!prefs) return null;

  const perShareRisk = Math.max(0, signal.entry - signal.stop_loss);
  const rewardPerShare = Math.max(0, signal.target - signal.entry);
  const riskAmount = (prefs.capital * prefs.risk_per_trade_pct) / 100;
  const qty = perShareRisk > 0 ? Math.floor(riskAmount / perShareRisk) : 0;
  const investment = qty * signal.entry;
  const maxLoss = qty * perShareRisk;
  const maxGain = qty * rewardPerShare;

  return (
    <div
      className="border-t border-subtle pt-3 mt-3"
      data-testid={`position-sizer-${signal.symbol}`}
    >
      {/* Header row with settings */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-mod">
          <Calculator size={11} weight="bold" />
          Position Sizer
        </div>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setEditing((v) => !v); }}
          data-testid={`position-edit-${signal.symbol}`}
          className="flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-muted-fg hover:text-primary-fg transition"
          title="Change capital / risk"
        >
          <PencilSimple size={10} />
          ₹{fmt(prefs.capital)} · risk {prefs.risk_per_trade_pct}%
        </button>
      </div>

      {editing ? (
        <form
          onSubmit={save}
          onClick={(e) => e.stopPropagation()}
          className="grid grid-cols-2 gap-2 mb-2"
        >
          <label className="text-[10px] font-mono uppercase tracking-widest text-muted-fg flex flex-col gap-1">
            Capital (₹)
            <input
              type="number"
              min="1000"
              step="1000"
              value={capital}
              onChange={(e) => setCapital(e.target.value)}
              data-testid="capital-input"
              className="px-2 py-1 border border-subtle rounded-sm bg-app text-primary-fg font-mono text-sm tabular-nums focus:border-focus outline-none"
            />
          </label>
          <label className="text-[10px] font-mono uppercase tracking-widest text-muted-fg flex flex-col gap-1">
            Risk per trade (%)
            <input
              type="number"
              min="0.1"
              max="10"
              step="0.1"
              value={riskPct}
              onChange={(e) => setRiskPct(e.target.value)}
              data-testid="risk-pct-input"
              className="px-2 py-1 border border-subtle rounded-sm bg-app text-primary-fg font-mono text-sm tabular-nums focus:border-focus outline-none"
            />
          </label>
          <div className="col-span-2 flex items-center gap-2">
            <button
              type="submit"
              disabled={saving}
              data-testid="save-prefs-btn"
              className="px-3 py-1.5 text-[10px] font-mono uppercase tracking-widest rounded-sm bg-pos-soft text-pos border border-pos-soft hover:opacity-90 transition"
            >
              {saving ? "Saving…" : "Save"}
            </button>
            <button
              type="button"
              onClick={() => setEditing(false)}
              className="px-3 py-1.5 text-[10px] font-mono uppercase tracking-widest rounded-sm text-secondary-fg hover:bg-surface-hover transition"
            >
              Cancel
            </button>
            {err && <span className="text-[10px] text-neg">{err}</span>}
          </div>
        </form>
      ) : qty > 0 ? (
        <div className={compact ? "grid grid-cols-3 gap-2" : "grid grid-cols-4 gap-2"}>
          <Stat label="Qty" value={fmt(qty)} accent="text-primary-fg" big />
          <Stat label="Invest" value={`₹${fmt(investment)}`} accent="text-primary-fg" />
          <Stat label="Max Loss" value={`-₹${fmt(maxLoss)}`} accent="text-neg" />
          {!compact && (
            <Stat label="Max Gain" value={`+₹${fmt(maxGain)}`} accent="text-pos" />
          )}
        </div>
      ) : (
        <div className="text-[10px] font-mono uppercase tracking-widest text-muted-fg">
          Risk too tight for this setup — entry near SL
        </div>
      )}
      {!editing && qty > 0 && (
        <div className="mt-2 text-[10px] font-mono uppercase tracking-widest text-muted-fg flex items-center gap-1">
          <span>{fmt(qty)} × ₹{fmt(signal.entry)}</span>
          <ArrowRight size={8} weight="bold" />
          <span className="text-neg">-₹{fmt(maxLoss)}</span>
          <span>/</span>
          <span className="text-pos">+₹{fmt(maxGain)}</span>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, accent = "text-primary-fg", big = false }) {
  return (
    <div>
      <div className="text-[10px] font-mono uppercase tracking-widest text-muted-fg">{label}</div>
      <div className={`${big ? "text-base" : "text-sm"} font-mono tabular-nums ${accent}`}>
        {value}
      </div>
    </div>
  );
}
